import { EntityWithStatusService } from './EntityWithStatusService';
import { ContextService } from './ContextService';
import { ICommitment, IEntityChildren } from '../types';
import { EntityStatus } from '../refData/EntityStatus';
import { AppGlobals } from '../AppGlobals';

export class CommitmentService extends EntityWithStatusService<ICommitment> {
    public readonly parentEntities = ['Directorate', 'LeadUser'];
    protected childrenEntities = ['Contributors($expand=ContributorUser)', 'Attributes($expand=AttributeType)'];

    constructor() {
        super(`/Commitments`);
    }

    public readMyCommitments(): Promise<ICommitment[]> {
        const username = encodeURIComponent(ContextService.Username());
        const filters = [
            `LeadUser/Username eq '${username}'`,
            `Contributors/any(c: c/ContributorUser/Username eq '${username}')`
        ];
        return this.readAll(
            `?$select=ID,Title,DirectorateID`
            + `&$filter=EntityStatusID eq ${EntityStatus.Open} and (${filters.join(' or ')})`
            + `&$orderby=Directorate/Title,Title`
        );
    }

    public readAllForList(includeClosedCommitments?: boolean, directorateID?: number): Promise<ICommitment[]> {
        return this.readAll(
            `?$select=ID,Title`
            + `&$orderby=Title`
            + `&$expand=EntityStatus($select=Title),Directorate($select=Title),LeadUser($select=Title),Contributors($select=ContributorUser;$expand=ContributorUser($select=Title)),Attributes($expand=AttributeType($select=Title))`
            + `&$filter=Directorate/CustomerID eq ${AppGlobals.CustomerID}`
            + (includeClosedCommitments ? `` : ` and EntityStatusID eq ${EntityStatus.Open}`)
            + (directorateID ? ` and DirectorateID eq ${directorateID}` : '')
        );
    }

    public async readForView(commitmentID: number): Promise<ICommitment> {
        return this.read(commitmentID, false, false, ['Directorate($select=Title)', 'LeadUser($select=Title,EmailAddress)', 'Contributors($select=ContributorUser;$expand=ContributorUser($select=Title,EmailAddress))', 'Attributes($expand=AttributeType($select=Title))']);
    }

    public readAllForLookup(includeClosedEntities?: boolean, additionalFields?: string): Promise<ICommitment[]> {
        return this.readAll(
            `?$select=ID,Title${additionalFields ? `,${additionalFields}` : ''}`
            + `&$orderby=Title&$filter=Directorate/CustomerID eq ${AppGlobals.CustomerID}`
            + (includeClosedEntities ? `` : ` and EntityStatusID eq ${EntityStatus.Open}`)
        );
    }

    public readDirectorateCommitments(directorateId: number): Promise<ICommitment[]> {
        return this.readAll(`?$expand=LeadUser,Attributes($expand=AttributeType),Contributors`
            + `&$filter=EntityStatusID eq ${EntityStatus.Open} and DirectorateID eq ${directorateId}&$orderby=Title`);
    }

    public async countCommitmentsForDirectorate(directorateID: number): Promise<number> {
        if (directorateID) {
            return (await this.readAll(`?$select=ID&$filter=EntityStatusID eq ${EntityStatus.Open} and DirectorateID eq ${directorateID}`))?.length;
        }
        return Promise.resolve(0);
    }

    public async entityChildren(id: number): Promise<IEntityChildren[]> {
        return this.entityChildrenSingle(id, 'Commitment updates', 'CommitmentUpdates', false);
    }
}